import { CaretRight, File, FolderSimple, GitBranch } from "@phosphor-icons/react";
import { Link, useSearch } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import type { GitConnection, GitTreeEntry } from "~/api/gitTypes";
import { useGitTree } from "~/api/queries";
import { repoExpansionKey, useRepoExpansion } from "~/features/git/expansion";
import { repositoryLabel } from "~/features/git/gitLabels";
import { Spinner } from "~/foundation/components/Spinner";
import { cn } from "~/foundation/utils/cn";

const ROW_CLASS =
  "flex h-8 min-w-0 flex-1 items-center gap-2 rounded-sm px-1.5 text-body text-sidebar-text " +
  "transition-colors duration-100 ease-in-out hover:bg-hover-bg motion-reduce:transition-none";

const ACTIVE_CLASS = "bg-sidebar-active-bg text-text hover:bg-sidebar-active-bg";

export interface RepositoryNodesProps {
  connections: GitConnection[];
  pathname: string;
  onNavigate?: (() => void) | undefined;
}

/**
 * The repository's structure in the menu (2026-09-24): one node per connection, and — once
 * disclosed — the folders and files below its prefix, one level per disclosure. Mirrors
 * `CollectionTree.tsx`: real disclosure buttons, real links, expansion persisted in the shell
 * store, and a folder's children only mount expanded, so a closed folder costs no call.
 */
export function RepositoryNodes({ connections, pathname, onNavigate }: RepositoryNodesProps) {
  const { t } = useTranslation();
  const { expanded, toggle } = useRepoExpansion();
  const search = useSearch({ strict: false }) as { path?: string };
  const currentPath = search.path ?? "";

  if (connections.length === 0) {
    return null;
  }

  return (
    <ul className="flex flex-col gap-0.5">
      {connections.map((connection) => {
        const key = repoExpansionKey(connection.id, "");
        const isExpanded = expanded[key] === true;
        const isActive = pathname === `/repo/${connection.id}` && currentPath === "";
        const childrenId = `repo-${connection.id}`;
        const label = repositoryLabel(connection);

        return (
          <li key={connection.id}>
            <div className="flex items-center gap-0.5">
              <DisclosureButton
                expanded={isExpanded}
                controls={childrenId}
                name={label}
                onClick={() => toggle(key)}
              />
              <Link
                to={"/repo/$connectionId" as never}
                params={{ connectionId: connection.id } as never}
                onClick={onNavigate}
                title={connection.remoteUrl}
                aria-current={isActive ? "page" : undefined}
                className={cn(ROW_CLASS, "font-medium", isActive && ACTIVE_CLASS)}
              >
                <GitBranch size={16} aria-hidden className="shrink-0 text-text-tertiary" />
                <span className="truncate">{label}</span>
                {connection.status === "failing" ? (
                  <span
                    role="img"
                    aria-label={t("Failing")}
                    title={connection.lastError ?? t("Failing")}
                    className="ms-auto h-1.5 w-1.5 shrink-0 rounded-full bg-danger"
                  />
                ) : null}
              </Link>
            </div>
            {isExpanded ? (
              <RepoFolder
                connection={connection}
                path=""
                listId={childrenId}
                pathname={pathname}
                currentPath={currentPath}
                onNavigate={onNavigate}
              />
            ) : null}
          </li>
        );
      })}
    </ul>
  );
}

function DisclosureButton({
  expanded,
  controls,
  name,
  onClick,
}: {
  expanded: boolean;
  controls: string;
  name: string;
  onClick: () => void;
}) {
  const { t } = useTranslation();
  return (
    <button
      type="button"
      onClick={onClick}
      aria-expanded={expanded}
      aria-controls={controls}
      aria-label={expanded ? t("Collapse {{name}}", { name }) : t("Expand {{name}}", { name })}
      className="flex h-6 w-6 shrink-0 items-center justify-center rounded-sm text-text-tertiary hover:bg-hover-bg hover:text-text"
    >
      <CaretRight
        size={12}
        aria-hidden
        className={cn(
          "transition-transform duration-100 motion-reduce:transition-none",
          expanded ? "rotate-90" : "rtl:rotate-180",
        )}
      />
    </button>
  );
}

/** Only mounted while disclosed — mounting is what makes the fetch lazy. */
function RepoFolder({
  connection,
  path,
  listId,
  pathname,
  currentPath,
  onNavigate,
}: {
  connection: GitConnection;
  path: string;
  listId: string;
  pathname: string;
  currentPath: string;
  onNavigate?: (() => void) | undefined;
}) {
  const { t } = useTranslation();
  const { data, isLoading, isError, error } = useGitTree(connection.id, path);
  const { expanded, toggle } = useRepoExpansion();

  if (isLoading) {
    return (
      <div id={listId} className="flex h-8 items-center ps-8">
        <Spinner size={14} label={t("Loading")} />
      </div>
    );
  }

  if (isError) {
    return (
      <p
        id={listId}
        title={error instanceof Error ? error.message : undefined}
        className="flex h-8 items-center ps-8 text-small text-danger"
      >
        {t("Could not read this folder")}
      </p>
    );
  }

  if (!data?.entries.length) {
    return (
      <p id={listId} className="flex h-8 items-center ps-8 text-small text-text-tertiary">
        {t("Empty folder")}
      </p>
    );
  }

  return (
    <ul id={listId} className="flex flex-col gap-0.5 ps-4">
      {data.entries.map((entry) => (
        <RepoEntry
          key={entry.path}
          connection={connection}
          entry={entry}
          pathname={pathname}
          currentPath={currentPath}
          expanded={expanded[repoExpansionKey(connection.id, entry.path)] === true}
          onToggle={() => toggle(repoExpansionKey(connection.id, entry.path))}
          onNavigate={onNavigate}
        />
      ))}
    </ul>
  );
}

function RepoEntry({
  connection,
  entry,
  pathname,
  currentPath,
  expanded,
  onToggle,
  onNavigate,
}: {
  connection: GitConnection;
  entry: GitTreeEntry;
  pathname: string;
  currentPath: string;
  expanded: boolean;
  onToggle: () => void;
  onNavigate?: (() => void) | undefined;
}) {
  const isActive = pathname === `/repo/${connection.id}` && currentPath === entry.path;
  const childrenId = `repo-${connection.id}-${entry.path.replace(/[^a-zA-Z0-9]+/g, "-")}`;

  if (entry.type === "file") {
    return (
      <li>
        <Link
          to={"/repo/$connectionId" as never}
          params={{ connectionId: connection.id } as never}
          search={{ path: entry.path, kind: "file" } as never}
          onClick={onNavigate}
          aria-current={isActive ? "page" : undefined}
          className={cn(ROW_CLASS, "ps-7", isActive && ACTIVE_CLASS)}
        >
          <File size={16} aria-hidden className="shrink-0 text-text-tertiary" />
          <span className="truncate">{entry.name}</span>
        </Link>
      </li>
    );
  }

  return (
    <li>
      <div className="flex items-center gap-0.5">
        <DisclosureButton
          expanded={expanded}
          controls={childrenId}
          name={entry.name}
          onClick={onToggle}
        />
        <Link
          to={"/repo/$connectionId" as never}
          params={{ connectionId: connection.id } as never}
          search={{ path: entry.path, kind: "dir" } as never}
          onClick={onNavigate}
          aria-current={isActive ? "page" : undefined}
          className={cn(ROW_CLASS, isActive && ACTIVE_CLASS)}
        >
          <FolderSimple size={16} aria-hidden className="shrink-0 text-text-tertiary" />
          <span className="truncate">{entry.name}</span>
        </Link>
      </div>
      {expanded ? (
        <RepoFolder
          connection={connection}
          path={entry.path}
          listId={childrenId}
          pathname={pathname}
          currentPath={currentPath}
          onNavigate={onNavigate}
        />
      ) : null}
    </li>
  );
}
